import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { shiftsAPI, usersAPI, shiftRequestsAPI } from '../../api/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CalendarView from './CalendarView';
import CalendarGrid from './CalendarGrid';
import UpcomingShifts from './UpcomingShifts';
import CustomDropdown from '../../components/CustomDropdown'; 
import CustomCalendar from '../../components/CustomCalendar';
import DateRangePicker from '../../components/DateRangePicker'; 
import './Shifts.css';



const Shifts = () => {
  const { user, isAdmin, isManager } = useAuth();
  const canManageShifts = isAdmin() || isManager();
  
  // Основные состояния
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('shifts-view-mode');
    return saved || 'calendar';
  });
  
  const [calendarView, setCalendarView] = useState(() => {
    const saved = localStorage.getItem('shifts-calendar-view');
    return saved || 'people';
  });

  // Кэш для данных
  const [shiftsCache, setShiftsCache] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Состояние для конфликтов
  const [conflicts, setConflicts] = useState([]);
  const [conflictsLoading, setConflictsLoading] = useState(false);

  // UI состояния
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date()
  });
  
  const [customDateRange, setCustomDateRange] = useState({
    startDate: new Date(),
    endDate: new Date()
  });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [selectedShiftForExchange, setSelectedShiftForExchange] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [exchangeData, setExchangeData] = useState({
    targetUserId: '',
    comment: ''
  });

  const exchangeQuillRef = useRef(null);
  const [newShift, setNewShift] = useState({
    shiftType: 'day',
    userId: user?.id || '',
    userName: user?.username || ''
  });

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  };

  const quillFormats = [
    'bold', 'italic', 'underline',
    'list', 'bullet'
  ];

  // Функция для форматирования даты в текст для пикера
  const getDateRangeDisplayText = useCallback(() => {
    const formatDate = (date) => {
      return date.toLocaleDateString('ru-RU');
    };
    
    if (showCustomDatePicker) {
      return `${formatDate(customDateRange.startDate)} - ${formatDate(customDateRange.endDate)}`;
    }
    
    return `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`;
  }, [dateRange, customDateRange, showCustomDatePicker]);

  // Обработчик выбора диапазона дат
  const handleDateRangeChange = useCallback((selectedRange) => {
    const today = new Date();
    
    switch (selectedRange) {
      case 'today':
        setDateRange({
          startDate: today,
          endDate: today
        });
        setShowCustomDatePicker(false);
        break;
        
      case 'week':
        const startOfWeek = new Date(today);
        const dayOfWeek = startOfWeek.getDay();
        const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(startOfWeek.getDate() + daysToMonday);
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        
        setDateRange({
          startDate: startOfWeek,
          endDate: endOfWeek
        });
        setShowCustomDatePicker(false);
        break;
        
      case 'month':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        
        setDateRange({
          startDate: startOfMonth,
          endDate: endOfMonth
        });
        setShowCustomDatePicker(false);
        break;
        
      case 'custom':
        setShowCustomDatePicker(true);
        break;
        
      default:
        break;
    }
  }, []);

  // Обработчик изменения кастомного диапазона
  const handleCustomDateRangeChange = useCallback((startDate, endDate) => {
    setCustomDateRange({ startDate, endDate });
    setDateRange({ startDate, endDate });
  }, []);

  // Обработчики смены вкладок с сохранением состояния
  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const handleCalendarViewChange = useCallback((view) => {
    setCalendarView(view);
  }, []);

  // Загрузка сотрудников (один раз)
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const usersResponse = await usersAPI.getAll();
        setEmployees(usersResponse.users || []);
      } catch (error) {
        console.error('Ошибка загрузки сотрудников:', error);
      }
    };

    loadEmployees();
  }, []);

  // Загрузка конфликтов при монтировании компонента
  useEffect(() => {
    if (canManageShifts) {
      loadConflicts();
    }
  }, [canManageShifts]);

  const loadConflicts = useCallback(async () => {
    if (!canManageShifts) return;
    
    setConflictsLoading(true);
    try {
      const response = await shiftsAPI.getConflicts();
      if (response.success) {
        setConflicts(response.conflicts || []);
      }
    } catch (error) {
      console.error('Ошибка при загрузке конфликтов:', error);
    } finally {
      setConflictsLoading(false);
    }
  }, [canManageShifts]);

  // Функция для создания времени смены
  const createShiftTimes = useCallback((dateString, shiftType) => {
    const date = new Date(dateString);
    const startTime = new Date(date);
    let endTime = new Date(date);

    let description = '';

    if (shiftType === 'day') {
      startTime.setHours(8, 0, 0, 0);
      endTime.setHours(20, 0, 0, 0);
      description = 'Дневная смена (08:00 - 20:00)';
    } else if (shiftType === 'day_9_18') {
      startTime.setHours(9, 0, 0, 0);
      endTime.setHours(18, 0, 0, 0);
      description = 'Дневная смена (09:00 - 18:00)';
    } else if (shiftType === 'day_9_21') {
      startTime.setHours(9, 0, 0, 0);
      endTime.setHours(21, 0, 0, 0);
      description = 'Дневная смена (09:00 - 21:00)';
    } else if (shiftType === 'day_11_20') {
      startTime.setHours(11, 0, 0, 0);
      endTime.setHours(20, 0, 0, 0);
      description = 'Дневная смена (11:00 - 20:00)';
    } else if (shiftType === 'day_11_23') {
      startTime.setHours(11, 0, 0, 0);
      endTime.setHours(23, 0, 0, 0);
      description = 'Дневная смена (11:00 - 23:00)';
    } else if (shiftType === 'night') {
      startTime.setHours(20, 0, 0, 0);
      endTime = new Date(startTime);
      endTime.setDate(endTime.getDate() + 1);
      endTime.setHours(8, 0, 0, 0);
      description = 'Ночная смена (20:00 - 08:00)';
    } else {
      startTime.setHours(0, 0, 0, 0);
      endTime.setHours(23, 59, 59, 999);

      const descriptions = {
        'vacation': 'Отпуск',
        'unpaid': 'Отпуск без сохранения заработной платы',
        'dayoff': 'Выходной день',
        'sick': 'Больничный лист',
        'holiday': 'День отдыха в счет дня работы в праздничный день'
      };
      description = descriptions[shiftType] || '';
    }

    return { startTime, endTime, description };
  }, []);

  const loadShiftsForMonth = useCallback(async (year, month, forceReload = false) => {
    const cacheKey = `${year}-${month}`;
    if (shiftsCache[cacheKey] && !forceReload) {
      return;
    }

    setLoading(true);
    
    try {
      // Загружаем смены только за нужный месяц (сервер добавляет буфер для ночных)
      const lastDay = new Date(year, month + 1, 0);
      const startStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const endStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;

      const shiftsResponse = await shiftsAPI.getByDateRange(startStr, endStr);
      const allShifts = shiftsResponse.shifts || [];
      
      // Загружаем запросы на обмен
      let allExchangeRequests = [];
      try {
        const requestsResponse = canManageShifts
          ? await shiftRequestsAPI.getAll()
          : await shiftRequestsAPI.getMyRequests();
        allExchangeRequests = requestsResponse.requests || [];
      } catch (error) {
        console.warn('Ошибка загрузки запросов на обмен:', error.message);
      }
      
      const allShiftsWithStatus = allShifts.map(shift => {
        const activeRequest = allExchangeRequests.find(request => 
          request.shift_id === shift.id && 
          ['pending', 'approved', 'rejected', 'cancelled'].includes(request.status)
        );
        
        return {
          ...shift,
          exchange_status: activeRequest ? activeRequest.status : null
        };
      });

      setShiftsCache(prev => ({
        ...prev,
        [cacheKey]: allShiftsWithStatus
      }));
      
    } catch (error) {
      console.error('Ошибка загрузки смен:', error);
      alert('Ошибка загрузки смен. Проверьте консоль для деталей.');
    } finally {
      setLoading(false);
    }
  }, [canManageShifts, shiftsCache]);

  // Получение смен для текущего месяца из кэша с учетом ночных смен
  const currentShifts = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const cacheKey = `${year}-${month}`;
    
    const allCachedShifts = shiftsCache[cacheKey] || [];
    
    // Фильтруем смены, которые должны отображаться в этом месяце
    const filteredShifts = allCachedShifts.filter(shift => {
      const shiftDate = new Date(shift.start_time);
      
      // 1. Смена начинается в этом месяце
      if (shiftDate.getFullYear() === year && shiftDate.getMonth() === month) {
        return true;
      }
      
      // 2. Для ночных смен: если они начинаются в конце предыдущего месяца
      // и заканчиваются в начале этого месяца, они тоже должны отображаться
      const shiftEndDate = new Date(shift.end_time);
      const isNightShift = shift.description?.includes('Ночная смена');
      
      if (isNightShift && shiftEndDate.getFullYear() === year && 
          shiftEndDate.getMonth() === month) {
        // Ночная смена заканчивается в этом месяце
        // Проверяем, не начинается ли она в предыдущем месяце
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevMonthYear = month === 0 ? year - 1 : year;
        
        if (shiftDate.getFullYear() === prevMonthYear && 
            shiftDate.getMonth() === prevMonth) {
          return true;
        }
      }
      
      return false;
    });
    
    return filteredShifts;
  }, [currentDate, shiftsCache]);

  // Загрузка данных при смене месяца
  useEffect(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    loadShiftsForMonth(year, month);
  }, [currentDate, loadShiftsForMonth]);

  // Обработчики событий
  const handleDateRangeChangeGlobal = useCallback((startDate, endDate) => {
    setDateRange({ startDate, endDate });
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (calendarView === 'month' || calendarView === 'people') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setDate(newDate.getDate() - 7);
      }
      return newDate;
    });
  }, [calendarView]);

  const goToNext = useCallback(() => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (calendarView === 'month' || calendarView === 'people') {
        newDate.setMonth(newDate.getMonth() + 1);
      } else {
        newDate.setDate(newDate.getDate() + 7);
      }
      return newDate;
    });
  }, [calendarView]);

  // Функция обновления комментария к смене
  const handleUpdateShiftComment = useCallback(async (shiftId, comment) => {
    try {
      await shiftsAPI.updateComment(shiftId, comment);
      
      // Обновляем кэш
      setShiftsCache(prev => {
        const newCache = { ...prev };
        Object.keys(newCache).forEach(key => {
          newCache[key] = newCache[key].map(shift => 
            shift.id === shiftId ? { ...shift, comment } : shift
          );
        });
        return newCache;
      });
      
    } catch (error) {
      console.error('Ошибка при обновлении комментария:', error);
      alert('Ошибка при сохранении комментария');
    }
  }, []);

  const handleDayClick = useCallback((day) => {
    if (!day) return;
    
    setSelectedDate(day.date);
    
    if (canManageShifts) {
      const year = day.date.getFullYear();
      const month = String(day.date.getMonth() + 1).padStart(2, '0');
      const date = String(day.date.getDate()).padStart(2, '0');
      const localDateString = `${year}-${month}-${date}`;
      
      setNewShift({
        shiftType: 'day',
        userId: user?.id || '',
        userName: user?.username || '',
        shiftDate: localDateString
      });
    }
    
    setShowAddModal(true);
  }, [canManageShifts, user]);

  const handleAddShiftButton = useCallback(() => {
    setSelectedDate(null);
    setNewShift({
      shiftType: 'day',
      userId: user?.id || '',
      userName: user?.username || '',
      shiftDate: ''
    });
    setShowAddModal(true);
  }, [user]);

  const handleEmployeeSelect = useCallback((employeeId) => {
    const selectedEmployee = employees.find(emp => emp.id === parseInt(employeeId));
    if (selectedEmployee) {
      // Автоматически проверяем наличие существующей смены
      const shiftDate = newShift.shiftDate ? new Date(newShift.shiftDate) : selectedDate;
      let existingShiftId = null;
      
      if (shiftDate) {
        const existingShift = currentShifts.find(shift => {
          const shiftDateObj = new Date(shift.start_time);
          return shiftDateObj.toDateString() === shiftDate.toDateString() && 
                shift.user_id === selectedEmployee.id;
        });
        
        existingShiftId = existingShift?.id || null;
      }
      
      setNewShift(prev => ({
        ...prev,
        userId: selectedEmployee.id,
        userName: selectedEmployee.username,
        existingShiftId: existingShiftId
      }));
    }
  }, [employees, newShift.shiftDate, selectedDate, currentShifts]);

  const handleAddShift = useCallback(async () => {
    if (!canManageShifts || !newShift.shiftDate || !newShift.userId) {
      return;
    }

    try {
      const shiftDate = new Date(newShift.shiftDate);
      const { startTime, endTime, description } = createShiftTimes(shiftDate, newShift.shiftType);

      if (endTime <= startTime) {
        return;
      }

      const shiftData = {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        description: description,
        userId: newShift.userId
      };

      await shiftsAPI.create(shiftData);
      
      // Очищаем кэш для текущего месяца
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const cacheKey = `${year}-${month}`;
      
      setShiftsCache(prev => ({
        ...prev,
        [cacheKey]: undefined
      }));
      
      // Перезагружаем данные
      await loadShiftsForMonth(year, month);
      
      setShowAddModal(false);
      setSelectedDate(null);
      setNewShift({
        shiftType: 'day',
        userId: user?.id || '',
        userName: user?.username || '',
        shiftDate: ''
      });

    } catch (error) {
      console.error('Error creating shift:', error);
    }
  }, [canManageShifts, newShift, createShiftTimes, currentDate, loadShiftsForMonth, user]);

  const handleDeleteShift = useCallback(async (shiftId) => {
    if (!canManageShifts) {
      alert('Только администраторы могут удалять смены');
      return;
    }

    if (!window.confirm('Вы уверены, что хотите удалить сотрудника из смены?')) {
      return;
    }

    try {
      await shiftsAPI.delete(shiftId);
      
      // Обновляем кэш
      setShiftsCache(prev => {
        const newCache = { ...prev };
        Object.keys(newCache).forEach(key => {
          newCache[key] = newCache[key].filter(shift => shift.id !== shiftId);
        });
        return newCache;
      });
      
      alert('Смена успешно удалена');
      
    } catch (error) {
      console.error('Ошибка при удалении смены:', error);
      alert('Ошибка при удалении смены');
    }
  }, [canManageShifts]);

  const canExchangeShift = useCallback((shift) => {
    return canManageShifts || shift.user_id === user?.id;
  }, [canManageShifts, user]);

  const isMyShift = useCallback((shift) => {
    return shift.user_id === user?.id;
  }, [user]);

  const handleExchangeRequest = useCallback((shift) => {
    if (!canExchangeShift(shift)) {
      alert('Вы можете отправлять запросы на обмен только для своих смен');
      return;
    }

    setSelectedShiftForExchange(shift);
    setExchangeData({
      targetUserId: '',
      comment: ''
    });
    setShowExchangeModal(true);
  }, [canExchangeShift]);

  const handleSubmitExchange = useCallback(async () => {
    if (!exchangeData.targetUserId) {
      alert('Выберите сотрудника для обмена');
      return;
    }

    try {
      const exchangeRequest = {
        shift_id: selectedShiftForExchange.id,
        to_user_id: exchangeData.targetUserId,
        comment: exchangeData.comment
      };

      await shiftRequestsAPI.create(exchangeRequest);
      
      alert('Запрос на обмен отправлен');
      setShowExchangeModal(false);
      setSelectedShiftForExchange(null);
      setExchangeData({
        targetUserId: '',
        comment: ''
      });

      // Перезагружаем данные для текущего месяца
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      await loadShiftsForMonth(year, month);

    } catch (error) {
      alert('Ошибка при отправке запроса на обмен');
    }
  }, [exchangeData, selectedShiftForExchange, currentDate, loadShiftsForMonth]);

  const handleMassAddShift = useCallback(async (shiftData) => {
    if (!canManageShifts) return;

    try {
      if (Array.isArray(shiftData.batch)) {
        const response = await shiftsAPI.batch(shiftData.batch);
        const errors = (response.results || []).filter(r => !r.success);
        if (errors.length > 0) {
          alert(`Не удалось сохранить ${errors.length} из ${shiftData.batch.length} смен.`);
          console.error('Batch errors:', errors);
        }
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        setShiftsCache(prev => {
          const newCache = { ...prev };
          delete newCache[`${year}-${month}`];
          return newCache;
        });
        await loadShiftsForMonth(year, month, true);
        return;
      }

      const { employeeId, date, shiftType, shiftId, delete: shouldDelete, batch, batchComplete } = shiftData;

      if (batchComplete) {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const cacheKey = `${year}-${month}`;

        setShiftsCache(prev => {
          const newCache = { ...prev };
          delete newCache[cacheKey];
          return newCache;
        });

        await loadShiftsForMonth(year, month, true);
        return;
      }

      if (shouldDelete && shiftId) {
        await shiftsAPI.delete(shiftId);
      } else {
        const employee = employees.find(emp => emp.id === employeeId);
        if (!employee) {
          console.error('Сотрудник не найден:', employeeId);
          throw new Error('Сотрудник не найден');
        }

        const shiftDate = new Date(date);
        const { startTime, endTime, description } = createShiftTimes(shiftDate, shiftType);

        const shiftDataToSend = {
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          description: description,
          userId: employeeId
        };

        if (shiftId) {
          await shiftsAPI.update(shiftId, shiftDataToSend);
        } else {
          await shiftsAPI.create(shiftDataToSend);
        }
      }

      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const cacheKey = `${year}-${month}`;

      if (!batch) {
        setShiftsCache(prev => {
          const newCache = { ...prev };
          delete newCache[cacheKey];
          return newCache;
        });

        await loadShiftsForMonth(year, month, true);
      }

    } catch (error) {
      console.error('Ошибка при массовом добавлении смены:', error);
      alert(`Ошибка: ${error.message}`);
      throw error;
    }
  }, [canManageShifts, employees, createShiftTimes, currentDate, loadShiftsForMonth]);

  // Вспомогательные функции
  const getPeriodName = () => {
    if (calendarView === 'month' || calendarView === 'people') {
      const month = currentDate.toLocaleDateString('ru-RU', { month: 'long' });
      const year = currentDate.getFullYear();
      
      // Преобразуем первую букву месяца в верхний регистр и добавляем "г." после года
      const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
      
      return `${capitalizedMonth} ${year} г.`;
    } else {
      const startOfWeek = new Date(currentDate);
      const dayOfWeek = startOfWeek.getDay();
      const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      startOfWeek.setDate(startOfWeek.getDate() + daysToMonday);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      return `${startOfWeek.toLocaleDateString('ru-RU')} - ${endOfWeek.toLocaleDateString('ru-RU')}`;
    }
  };

  // Мемоизированные данные для UI
  const filteredEmployeesForModal = useMemo(() => {
    if (!searchTerm) return employees;
    
    return employees.filter(employee => 
      employee.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.position && employee.position.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (employee.department && employee.department.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [searchTerm, employees]);

  // Получение смен для выбранной даты
  const selectedDateShifts = useMemo(() => {
    if (!selectedDate) return [];
    
    return currentShifts.filter(shift => {
      const shiftDate = new Date(shift.start_time);
      return shiftDate.toDateString() === selectedDate.toDateString();
    });
  }, [selectedDate, currentShifts]);

  // Группировка смен по отделам для модального окна
  const existingShiftsByDepartment = useMemo(() => {
    const grouped = {};
    
    selectedDateShifts.forEach(shift => {
      let department = 'Без отдела';
      const employee = employees.find(emp => emp.id === shift.user_id);
      if (employee) {
        department = employee.department || 'Без отдела';
      } else if (shift.user_department) {
        department = shift.user_department;
      }
      
      if (!grouped[department]) {
        grouped[department] = [];
      }
      grouped[department].push(shift);
    });

    return grouped;
  }, [selectedDateShifts, employees]);

  return (
    <div className="shifts-container">
      {/* Заголовок */}
      <div className="content-page-header">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Поиск по сотрудникам..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="clear-search"
              onClick={() => setSearchTerm('')}
              title="Очистить поиск"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <div className="view-toggle">
          <button 
            className={`toggle-btn ${viewMode === 'calendar' ? 'active' : ''}`}
            onClick={() => setViewMode('calendar')}
          >
            <i className="fas fa-calendar-alt"></i>
            Календарь смен
          </button>
          <button 
            className={`toggle-btn ${viewMode === 'upcoming' ? 'active' : ''}`}
            onClick={() => setViewMode('upcoming')}
          >
            <i className="fas fa-list"></i>
            Список смен
          </button>
        </div>

        {canManageShifts && (
          <div className="button_header_shifts">
            <button 
              className="add-button"
              onClick={handleAddShiftButton} 
            >
              <i className="fas fa-plus"></i>
              Добавить смену
            </button>
          </div>
        )}
      </div>

      {viewMode === 'calendar' ? (
        <div className="calendar-view">
          <div className="calendar-controls">
            <div className="view-toggle">
              <button 
                className={`view-toggle-btn ${calendarView === 'people' ? 'active' : ''}`}
                onClick={() => setCalendarView('people')}
              >
                Сотрудники
              </button>
              <button 
                className={`view-toggle-btn ${calendarView === 'month' ? 'active' : ''}`}
                onClick={() => setCalendarView('month')}
              >
                Месяц
              </button>
            </div>
            
            <h2 className="calendar-title">{getPeriodName()}</h2>
            
            <div className="calendar-navigation">
              <button className="calendar-nav-btn" onClick={goToPrevious}>
                <i className="fas fa-chevron-left"></i>
              </button>
              
              <button className="calendar-nav-btn" onClick={goToNext}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading">
              <i className="fas fa-spinner fa-spin"></i>
              Загрузка...
            </div>
          ) : calendarView === 'people' ? (
            <CalendarView 
              shifts={currentShifts}
              employees={employees}
              currentDate={currentDate}
              searchTerm={searchTerm}
              onDateChange={setCurrentDate}
              onDayClick={handleDayClick}
              onAddShift={handleMassAddShift}
              isAdmin={canManageShifts}
              onUpdateShiftComment={handleUpdateShiftComment}
            />
          ) : calendarView === 'month' || calendarView === 'week' ? (
            <CalendarGrid 
              shifts={currentShifts}
              employees={employees}
              currentDate={currentDate}
              calendarView={calendarView}
              searchTerm={searchTerm}
              onDayClick={handleDayClick}
            />
          ) : (
            <div className="calendar-placeholder">
              <p>Выберите тип отображения календаря</p>
            </div>
          )}
        </div>
      ) : (
        <UpcomingShifts 
          shifts={currentShifts}
          employees={employees}
          isAdmin={canManageShifts}
          searchTerm={searchTerm}
          onExchangeRequest={handleExchangeRequest}
          onDeleteShift={handleDeleteShift}
        />
      )}

      {/* Модальное окно добавления смены */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>
                {selectedDate 
                  ? `Смены на ${selectedDate?.toLocaleDateString('ru-RU')}` 
                  : 'Добавить смену'
                }
              </h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-content">
              {selectedDate && selectedDateShifts.length > 0 ? (
                <div className="existing-shifts">
                  <h4>Сотрудники на смене:</h4>
                  {Object.keys(existingShiftsByDepartment).length > 0 ? (
                    Object.entries(existingShiftsByDepartment).map(([department, departmentShifts]) => (
                      <div key={department} className="department-group">
                        <div className="department-header">
                          <i className="fas fa-building"></i>
                          <h5>{department}</h5>
                          <span className="shift-count">{departmentShifts.length} смен</span>
                        </div>
                        <div className="department-shifts">
                          {departmentShifts.map(shift => {
                              const startTime = new Date(shift.start_time);
                              const endTime = new Date(shift.end_time);
                              const startHours = startTime.getHours();
                              const endHours = endTime.getHours();
                              const isDayShift = startHours === 8 && endHours === 20;
                              const isDay9Shift = startHours === 9 && endHours === 18;
                              const isDay9_21Shift = startHours === 9 && endHours === 21;
                              const isDay11_20Shift = startHours === 11 && endHours === 20;
                              const isDay11_23Shift = startHours === 11 && endHours === 23;
                              const isNightShift = startHours === 20;
                            
                            // Используем описание из смены для отображения
                            const getShiftDisplayName = () => {
                              if (shift.description) {
                                return shift.description;
                              }
                              // Если description нет, определяем по часам
                                if (isDayShift) return 'Дневная смена (08:00 - 20:00)';
                                if (isDay9Shift) return 'Дневная смена (09:00 - 18:00)';
                                if (isDay9_21Shift) return 'Дневная смена (09:00 - 21:00)';
                                if (isDay11_20Shift) return 'Дневная смена (11:00 - 20:00)';
                                if (isDay11_23Shift) return 'Дневная смена (11:00 - 23:00)';
                                if (isNightShift) return 'Ночная смена (20:00 - 08:00)';
                              return 'Смена';
                            };

                            return (
                              <div key={shift.id} className="shift-card">
                                <div className="shift-info">
                                  <div className="shift-main">
                                    <div className="shift-date">
                                      <strong>{startTime.toLocaleDateString('ru-RU')}</strong>
                                      <span className={`shift-type-badge ${
                                        isDayShift ? 'day-shift' : 
                                        isDay9Shift ? 'day8-shift' : 
                                        isNightShift ? 'night-shift' :
                                        'other-shift'
                                      }`}>
                                        {getShiftDisplayName()}
                                      </span>
                                    </div>
                                    
                                    <div className="employee-info">
                                      <div className="employee-name">
                                        {shift.user_name}
                                      </div>
                                      {shift.user_position && (
                                        <div className="employee-position">{shift.user_position}</div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Комментарий к смене */}
                                  {shift.comment && (
                                    <div className="shift-comment-section">
                                      <div className="shift-comment-header">
                                        <i className="fas fa-comment"></i>
                                        <strong>Комментарий к смене:</strong>
                                      </div>
                                      <div className="shift-comment-content">
                                        {(shift.comment)}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-shifts-today">
                      <i className="fas fa-users-slash"></i>
                      <p>На этот день смен нет</p>
                    </div>
                  )}
                </div>
              ) : selectedDate ? (
                <div className="no-shifts-today">
                  <i className="fas fa-users-slash"></i>
                  <p>На этот день смен нет</p>
                </div>
              ) : null}

              {canManageShifts && (
                <div className="add-shift-form">
                  <h4>{selectedDate ? 'Добавить еще смену' : 'Добавить новую смену'}</h4>
                  
                  <div className="form-group">
                    <label className="form-label">Дата смены:</label>
                    <CustomCalendar
                      value={newShift.shiftDate ? new Date(newShift.shiftDate) : (selectedDate || null)}
                      onChange={(date) => {
                        if (date) {
                          const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                          setNewShift(prev => ({ ...prev, shiftDate: formattedDate }));
                        } else {
                          setNewShift(prev => ({ ...prev, shiftDate: '' }));
                        }
                      }}
                    />
                  </div>

                  {/* КАСТОМНЫЙ ПИКЕР - Тип смены */}
                  <div className="form-group">
                    <label className="form-label">Тип смены:</label>
                    <CustomDropdown
                      options={[
                        { value: 'day', label: 'Дневная смена (08:00 - 20:00)' },
                        { value: 'day_9_18', label: 'Дневная смена (09:00 - 18:00)' },
                        { value: 'day_9_21', label: 'Дневная смена (09:00 - 21:00)' },
                        { value: 'day_11_20', label: 'Дневная смена (11:00 - 20:00)' },
                        { value: 'day_11_23', label: 'Дневная смена (11:00 - 23:00)' },
                        { value: 'night', label: 'Ночная смена (20:00 - 08:00)' },
                        { value: 'vacation', label: 'Отпуск' },
                        { value: 'unpaid', label: 'Отпуск без сохранения заработной платы' },
                        { value: 'dayoff', label: 'Выходной день' },
                        { value: 'sick', label: 'Больничный лист' },
                        { value: 'holiday', label: 'День отдыха в счет дня работы в праздничный день' }
                      ]}
                      value={newShift.shiftType}
                      onChange={(value) => setNewShift(prev => ({ ...prev, shiftType: value }))}
                      placeholder="Выберите тип смены..."
                    />
                  </div>
                  
                  {/* КАСТОМНЫЙ ПИКЕР - Сотрудник */}
                  <div className="form-group">
                    <label className="form-label">Сотрудник:</label>
                    <CustomDropdown
                      options={filteredEmployeesForModal.map(employee => ({
                        value: employee.id,
                        label: `${employee.username} - ${employee.position} (${employee.department || 'Без отдела'})`
                      }))}
                      value={newShift.userId}
                      onChange={(value) => handleEmployeeSelect(value)}
                      placeholder="Выберите сотрудника..."
                    />
                  </div>
                </div>
              )}
            </div>
            
            {canManageShifts && (
              <div className="modal-footer">
                <button 
                  className="add-button" 
                  onClick={handleAddShift}
                  disabled={!newShift.shiftDate || !newShift.userId}
                >
                  <i className="fas fa-plus"></i>
                  Добавить смену
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно запроса на обмен */}
      {showExchangeModal && selectedShiftForExchange && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Запрос на обмен сменой</h3>
              <button className="close-btn" onClick={() => setShowExchangeModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-content">
              <div className="exchange-shift-info">
                <h4>Выбранная смена:</h4>
                <div className="selected-shift">
                  <strong>
                    {new Date(selectedShiftForExchange.start_time).toLocaleDateString('ru-RU')} - 
                    {(() => {
                      const hours = new Date(selectedShiftForExchange.start_time).getHours();
                      if (hours === 8) return ' Дневная (08:00-20:00)';
                      if (hours === 9) return ' Дневная (09:00-18:00)';
                      if (hours === 20) return ' Ночная (20:00-08:00)';
                      return ' Смена';
                    })()}
                  </strong>
                  <div>{selectedShiftForExchange.user_name}</div>
                </div>
              </div>

              {/* КАСТОМНЫЙ ПИКЕР - Сотрудник для обмена */}
              <div className="form-group">
                <label className="form-label">Сотрудник для обмена:</label>
                <CustomDropdown
                  options={filteredEmployeesForModal
                    .filter(emp => emp.id !== user?.id)
                    .map(employee => ({
                      value: employee.id,
                      label: `${employee.username} - ${employee.position} (${employee.department || 'Без отдела'})`
                    }))
                  }
                  value={exchangeData.targetUserId}
                  onChange={(value) => setExchangeData(prev => ({ ...prev, targetUserId: value }))}
                  placeholder="Выберите сотрудника для обмена..."
                />
              </div>

              <div className="form-group">
                <label className="form-label">Комментарий:</label>
                <ReactQuill
                  ref={exchangeQuillRef}
                  value={exchangeData.comment}
                  onChange={(value) => setExchangeData(prev => ({ ...prev, comment: value }))}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Дополнительная информация..."
                  theme="snow"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="add-button" 
                onClick={handleSubmitExchange}
                disabled={!exchangeData.targetUserId}
              >
                <i className="fas fa-paper-plane"></i>
                Отправить запрос
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Уведомление о конфликтах смен */}
      {canManageShifts && conflicts.length > 0 && (
        <div className="conflicts-warning">
          <div className="warning-header">
            <i className="fas fa-exclamation-triangle"></i>
            <h4>Обнаружены конфликты смен</h4>
            <span className="conflict-count">({conflicts.length})</span>
          </div>
          <div className="conflict-list">
            {conflicts.map((conflict, index) => {
              const employee = employees.find(emp => emp.id === conflict.user_id);
              return (
                <div key={index} className="conflict-item">
                  <div className="conflict-user">
                    <strong>{employee?.username || `ID: ${conflict.user_id}`}</strong>
                  </div>
                  <div className="conflict-shifts">
                    {conflict.shifts.map((shift, idx) => (
                      <div key={idx} className="conflict-shift">
                        <div className="shift-info">
                          <span className="shift-time">
                            {new Date(shift.start_time).toLocaleDateString('ru-RU')}{' '}
                            {new Date(shift.start_time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} -{' '}
                            {new Date(shift.end_time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <span className="shift-desc">{shift.description}</span>
                        </div>
                        <button 
                          className="delete-shift-btn"
                          onClick={async () => {
                            if (window.confirm('Вы уверены, что хотите удалить эту смену?')) {
                              try {
                                await shiftsAPI.delete(shift.id);
                                // После удаления обновляем список конфликтов
                                await loadConflicts();
                              } catch (error) {
                                console.error('Ошибка при удалении смены:', error);
                                alert('Ошибка при удалении смены');
                              }
                            }
                          }}
                          title="Удалить смену"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="warning-footer">
            <small>Рекомендуется вручную исправить конфликты</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shifts;
